// Pretext wrapper: cached prepares, cursor → offset mapping, line layout (plain and rich-inline).
// Browser only (Pretext measures with canvas).

import {
  measureNaturalWidth,
  prepareWithSegments,
  walkLineRanges,
  type LayoutCursor,
  type PreparedTextWithSegments,
} from '@chenglou/pretext';
import { prepareRichInline, walkRichInlineLineRanges, type PreparedRichInline } from '@chenglou/pretext/rich-inline';
import type { BoldRun } from './text';
import { richItems } from './text';

export interface Line {
  /** Offsets into the laid-out text; `end` excludes trailing spaces, `start` skips leading spaces. */
  start: number;
  end: number;
  width: number;
}

interface Prepared {
  text: string;
  prepared: PreparedTextWithSegments;
  offsets: number[] | null;
}

const MAX_CACHE = 50_000;
const cache = new Map<string, Prepared>();

export function clearMeasureCache(): void {
  cache.clear();
}

export function prepare(text: string, font: string): Prepared {
  const key = font + '\u0000' + text;
  let p = cache.get(key);
  if (!p) {
    if (cache.size >= MAX_CACHE) cache.clear();
    p = { text, prepared: prepareWithSegments(text, font), offsets: null };
    cache.set(key, p);
  }
  return p;
}

export function naturalWidth(text: string, font: string): number {
  if (!text) return 0;
  return measureNaturalWidth(prepare(text, font).prepared);
}

/** Width of a suffix as it adds to preceding text: width("x" + s) − width("x") (Pitfall M3). */
export function suffixWidth(suffix: string, font: string): number {
  return naturalWidth('x' + suffix, font) - naturalWidth('x', font);
}

/** Start offset of each segment within `text`, realigning with indexOf when segments don't concatenate back. */
function segmentOffsets(text: string, segments: string[]): number[] {
  const out = new Array<number>(segments.length + 1);
  let pos = 0;
  for (let i = 0; i < segments.length; i++) {
    const seg = segments[i];
    if (text.startsWith(seg, pos)) {
      out[i] = pos;
      pos += seg.length;
    } else {
      const found = text.indexOf(seg, pos);
      if (found >= 0) {
        out[i] = found;
        pos = found + seg.length;
      } else {
        out[i] = pos;
      }
    }
  }
  out[segments.length] = pos;
  return out;
}

let graphemeSegmenter: Intl.Segmenter | null = null;

function graphemePrefixLength(segment: string, graphemes: number): number {
  if (graphemes <= 0) return 0;
  graphemeSegmenter ??= new Intl.Segmenter(undefined, { granularity: 'grapheme' });
  let n = 0;
  for (const g of graphemeSegmenter.segment(segment)) {
    if (n === graphemes) return g.index;
    n++;
  }
  return segment.length;
}

function cursorOffset(text: string, prepared: PreparedTextWithSegments, offsets: number[], c: LayoutCursor): number {
  const segs = prepared.segments;
  if (c.segmentIndex >= segs.length) return offsets[segs.length] ?? text.length;
  return offsets[c.segmentIndex] + graphemePrefixLength(segs[c.segmentIndex], c.graphemeIndex);
}

function trimLine(text: string, start: number, end: number): [number, number] {
  while (start < end && text.charCodeAt(start) === 0x20) start++;
  while (end > start && text.charCodeAt(end - 1) === 0x20) end--;
  return [start, end];
}

/** All lines of `text` at `maxWidth` (plain, one font). */
export function layoutLines(text: string, font: string, maxWidth: number): Line[] {
  if (!text) return [];
  const p = prepare(text, font);
  p.offsets ??= segmentOffsets(text, p.prepared.segments);
  const offsets = p.offsets;
  const lines: Line[] = [];
  walkLineRanges(p.prepared, maxWidth, range => {
    const s = cursorOffset(text, p.prepared, offsets, range.start);
    const e = cursorOffset(text, p.prepared, offsets, range.end);
    const [start, end] = trimLine(text, s, e);
    lines.push({ start, end, width: range.width });
  });
  return lines;
}

/** End offset (trimmed) of the first line of `text` at `maxWidth`. */
export function firstLineEnd(text: string, font: string, maxWidth: number): number {
  const lines = layoutLines(text, font, maxWidth);
  return lines.length ? lines[0].end : 0;
}

interface RichItemPrepared {
  start: number; // offset of the item's trimmed text within the full text
  prepared: PreparedTextWithSegments;
  offsets: number[];
}

type InternalRich = { items?: Array<{ prepared?: PreparedTextWithSegments } | undefined> };

/** Lines of `text` with bold runs laid out in `boldFont` (rich-inline). */
export function layoutRichLines(text: string, runs: BoldRun[], font: string, boldFont: string, maxWidth: number): Line[] {
  if (!text) return [];
  if (runs.length === 0) return layoutLines(text, font, maxWidth);
  const items = richItems(text, runs, font, boldFont);
  const rich: PreparedRichInline = prepareRichInline(items.map(i => ({ text: i.text, font: i.font })));
  const internal = (rich as unknown as InternalRich).items;
  const itemPrepared: (RichItemPrepared | null)[] = items.map((item, i) => {
    let lead = 0;
    while (lead < item.text.length && item.text.charCodeAt(lead) === 0x20) lead++;
    const trimmed = item.text.slice(lead).replace(/ +$/, '');
    if (!trimmed) return null;
    // 0.0.9 keeps each item's prepared text at items[i].prepared (internal). Fall back to re-preparing.
    const prepared = internal?.[i]?.prepared ?? prepareWithSegments(trimmed, item.font);
    return { start: item.start + lead, prepared, offsets: segmentOffsets(trimmed, prepared.segments) };
  });
  const offsetOf = (itemIndex: number, c: LayoutCursor): number => {
    const ip = itemPrepared[itemIndex];
    if (!ip) return items[itemIndex].start;
    const trimmed = text.slice(ip.start, ip.start + (ip.offsets[ip.prepared.segments.length] ?? 0));
    return ip.start + cursorOffset(trimmed, ip.prepared, ip.offsets, c);
  };
  const lines: Line[] = [];
  walkRichInlineLineRanges(rich, maxWidth, range => {
    const frags = range.fragments.filter(f => itemPrepared[f.itemIndex]);
    if (frags.length === 0) return;
    const first = frags[0];
    const last = frags[frags.length - 1];
    const [start, end] = trimLine(text, offsetOf(first.itemIndex, first.start), offsetOf(last.itemIndex, last.end));
    lines.push({ start, end, width: range.width });
  });
  return lines;
}
